/* eslint-disable react/prop-types */
/* eslint-disable react/destructuring-assignment */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { defaultErrorMessage } from '../../constants';
import ErrorModal from '../Shared/ErrorModal';
import LatestModalDesign from '../Shared/LatestModalDesign';
import LoadingOverlay from '../Shared/LoadingOverlay';
import RadioOptions from '../Shared/RadioOptions';
import CheckboxOptions from '../Shared/CheckboxOptions';
import { updateAddressOfCurrentUser } from '../Shared/Helpers';

class FinishAddressModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      showErrorModal: false,
      error: null,
      addressDetails: {
        apt_number: '',
        parking_type: '',
        parking_info: '',
        address_type: 'home',
        name: '',
        default: false,
      },
      ctaDisabled: true,
      width: window.innerWidth,
    };

    this.handleSave = this.handleSave.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.closeErrorModal = this.closeErrorModal.bind(this);
    this.setFieldValue = this.setFieldValue.bind(this);
    this.validateRequiredFields = this.validateRequiredFields.bind(this);
    this.handleDefaultChange = this.handleDefaultChange.bind(this);
  }

  componentDidMount() {
    // Initialize with existing address data if editing
    if (this.props.address) {
      this.setState({
        addressDetails: {
          apt_number: this.props.address.apt_number || '',
          parking_type: this.props.address.parking_type || '',
          parking_info: this.props.address.parking_info || '',
          address_type: this.props.address.address_type || 'home',
          name: this.props.address.name || '',
          default: this.props.address.default || false,
        },
      }, () => {
        this.validateRequiredFields();
      });
    }
    window.addEventListener('resize', this.updateWindowDimensions);
  }

  updateWindowDimensions() {
    this.setState({ width: window.innerWidth });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  setFieldValue(field, value) {
    this.setState(prevState => ({
      addressDetails: {
        ...prevState.addressDetails,
        [field]: value,
      },
    }), () => {
      this.validateRequiredFields();
    });
  }

  handleDefaultChange(event) {
    this.setFieldValue('default', event.target.checked);
  }

  validateRequiredFields() {
    const { addressDetails } = this.state;
    const { parking_type, parking_info } = addressDetails;
    
    // Required fields: parking_type and parking_info
    const isValid = parking_type && parking_info && parking_info.trim().length > 0;
    
    this.setState({ ctaDisabled: !isValid });
  }

  handleClose() {
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  handleSave(event) {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    const { addressDetails, ctaDisabled } = this.state;
    
    if (ctaDisabled) {
      return;
    }

    this.setState({ loading: true });

    const addressId = this.props.address?.id;
    
    if (!addressId) {
      this.setState({
        loading: false,
        showErrorModal: true,
        error: 'Address ID is required',
      });
      return;
    }

    // Prepare payload with default setting
    const payload = {
      ...addressDetails,
      default: addressDetails.default,
    };

    updateAddressOfCurrentUser(
      addressId,
      payload,
      // Success callback
      (response) => {
        this.setState({ loading: false });
        const { success, result } = response.data;

        if (success || result) {
          // Call parent's success callback to refresh the list
          if (this.props.onSuccess) {
            this.props.onSuccess(response.data);
          }
          // Close the modal
          this.handleClose();
        } else {
          const message = get(response, 'data.errors.message', defaultErrorMessage);
          this.setState({ showErrorModal: true, error: message });
        }
      },
      // Error callback
      (error) => {
        this.setState({ loading: false });
        if (error && error.response && error.response.data) {
          const message = get(error.response.data, 'errors.0.message', defaultErrorMessage);
          this.setState({ showErrorModal: true, error: message });
        } else {
          this.setState({
            showErrorModal: true,
            error: defaultErrorMessage,
          });
        }
      }
    );
  }

  closeErrorModal() {
    this.setState({ showErrorModal: false, error: null });
  }

  renderAddressHeader() {
    const { address } = this.props;
    if (!address) return null;

    const formattedAddress = [
      address.city,
      address.state,
      address.zip_code,
      address.country,
    ].filter(Boolean).join(', ');

    return (
      <div className="mb-24">
        <div className="size-18-24 contentPrimary font-weight-bold mb-8">
          {address.address_line_1}
        </div>
        <div className="size-14-20 contentSecondary">
          {formattedAddress}
        </div>
        {address.address_line_2 && (
          <div className="size-14-20 contentSecondary">
            {address.address_line_2}
          </div>
        )}
      </div>
    );
  }

  renderParkingOptions() {
    const { addressDetails } = this.state;
    
    const parkingOptions = [
      { label: 'Free street parking', value: 'free_street_parking' },
      { label: 'Metered street parking', value: 'metered_parking' },
      { label: 'Lot or garage', value: 'parking_lot_or_garage' },
      { label: 'Valet service', value: 'valet_service' },
    ];

    const formattedOptions = parkingOptions.map(option => ({
      ...option,
      checked: option.value === addressDetails.parking_type,
    }));

    return (
      <div className="mb-24">
        <label className="size-14-20 contentPrimary font-weight-bold mb-8 d-block">
          Parking Details *
        </label>
        <RadioOptions
          name="parking_type"
          optionsArray={formattedOptions}
          onChange={(value) => this.setFieldValue('parking_type', value)}
          defaultValue={addressDetails.parking_type}
          required
        />
        <div className="size-12-16 contentSecondary mt-4">
          Please note: Additional charges may apply if your Provider is required to pay for parking.
        </div>
      </div>
    );
  }

  renderAddressTypeOptions() {
    const { addressDetails } = this.state;
    
    const typeOptions = [
      { label: 'Private residence', value: 'home' },
      { label: 'Business/Office', value: 'office' },
      { label: 'Hotel', value: 'hotel' },
    ];

    const formattedOptions = typeOptions.map(option => ({
      ...option,
      checked: option.value === addressDetails.address_type,
    }));

    return (
      <div className="mb-24">
        <label className="size-14-20 contentPrimary font-weight-bold mb-8 d-block">
          Address Type
        </label>
        <RadioOptions
          name="address_type"
          optionsArray={formattedOptions}
          onChange={(value) => this.setFieldValue('address_type', value)}
          defaultValue={addressDetails.address_type}
        />
      </div>
    );
  }

  renderDefaultCheckbox() {
    const { addressDetails } = this.state;
    const { address } = this.props;
    
    // Show checkbox for new addresses (no address.id) or addresses that are not default
    // Only hide if explicitly set as default (true), show for undefined/false/null
    if (address && address.default === true) {
      return null;
    }

    const checkboxOptions = [{
      key: 'default',
      label: 'Set as default address',
      checked: addressDetails.default
    }];

    return (
      <div className="mb-24">
        <CheckboxOptions
          optionsArray={checkboxOptions}
          onChange={(key, checked) => this.setFieldValue('default', checked)}
        />
        <div className="size-12-16 contentSecondary mt-4">
          This address will be automatically selected for future bookings.
        </div>
      </div>
    );
  }

  render() {
    const { isOpen } = this.props;
    const { loading, showErrorModal, error, addressDetails, ctaDisabled, width } = this.state;

    return (
      <>
        <LatestModalDesign
          isOpen={isOpen}
          title="Finish Address Details"
          close={this.handleClose}
          apply={this.handleSave}
          applyBtnCopy="Save Address"
          cancelBtnCopy="Cancel"
          disabled={loading || ctaDisabled}
          contentExtraClass="finish-address-modal"
          fullWidthFooter={width < 800}
        >
          {this.renderAddressHeader()}

          <div className="row">
            <div className="col-xs-12 mb-16">
              <label className="size-14-20 contentPrimary font-weight-bold mb-8 d-block">
                Address Name
              </label>
              <input
                type="text"
                value={addressDetails.name}
                className="generic-input-style-white size-16-24 p-12"
                onChange={(event) => this.setFieldValue('name', event.target.value)}
                placeholder="e.g., Home, Work, Mom's House"
              />
            </div>

            <div className="col-xs-12 mb-16">
              <label className="size-14-20 contentPrimary font-weight-bold mb-8 d-block">
                Apt / Suite / Hotel name & room
                <span className="contentSecondary font-weight-normal"> (optional)</span>
              </label>
              <input
                type="text"
                value={addressDetails.apt_number}
                className="generic-input-style-white size-16-24 p-12"
                onChange={(event) => this.setFieldValue('apt_number', event.target.value)}
                placeholder="Apartment, suite, or room number"
              />
            </div>

            <div className="col-xs-12 mb-16">
              {this.renderParkingOptions()}
            </div>

            <div className="col-xs-12 mb-16">
              <label className="size-14-20 contentPrimary font-weight-bold mb-8 d-block">
                Arrival Instructions *
              </label>
              <textarea
                className="generic-textarea-style-white size-16-24 p-12"
                required
                rows={4}
                value={addressDetails.parking_info}
                onChange={(event) => this.setFieldValue('parking_info', event.target.value)}
                placeholder="Let your Provider know about hotel name, gate codes, parking passes, complex walking directions, or anything else to help them find you when they arrive."
                style={{
                  resize: 'vertical',
                  minHeight: '100px',
                  fontFamily: 'inherit'
                }}
              />
              <div className="size-12-16 contentSecondary mt-4">
                Arrival instructions help your Provider know exactly what to expect when they show up to your appointment.
              </div>
            </div>

            <div className="col-xs-12 mb-16">
              {this.renderAddressTypeOptions()}
            </div>

            <div className="col-xs-12 mb-16">
              {this.renderDefaultCheckbox()}
            </div>
          </div>
        </LatestModalDesign>

        {showErrorModal && (
          <ErrorModal isOpen={showErrorModal} close={this.closeErrorModal}>
            <p>{error}</p>
          </ErrorModal>
        )}

        <LoadingOverlay showLoader={loading} />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  client: state.client,
});

FinishAddressModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  address: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default connect(mapStateToProps)(FinishAddressModal);
